import React, { useEffect, useState, useRef, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchAllTataRecords,
  createTataRecord,
  updateTataRecord,
  deleteTataRecord,
  bulkDeleteTataRecords,
  importTataRecords,
  exportTataRecords,
  syncTataTaskStatus,
  getFieldExecutives,
} from "../actions/TataAction";
import Button from "../components/Button";
import Loading from "../components/Loading";
import Pagination from "../components/Pagination";
import Table from "../components/Table";
import DriveFilters, {
  driveFilterSelectClass,
} from "../components/DriveFilters";
import {
  Edit,
  Trash2,
  Upload,
  Download,
  Check,
  X,
  Trash,
  Eye,
  Copy,
} from "lucide-react";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useNavigate } from "react-router-dom";

const TataPage = () => {
  const dispatch = useDispatch();
  const { getTataRecordsData, loadingGetTataRecords } = useSelector(
    (state) => state.getTataRecords || {},
  );

  // Extract data and total from the new structure
  const [data, setData] = useState(getTataRecordsData?.data || []);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [lastRefresh, setLastRefresh] = useState(new Date());
  const intervalRef = useRef(null);
  const total = getTataRecordsData?.total || 0;
  const loading = loadingGetTataRecords || false;
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [file, setFile] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [formData, setFormData] = useState({});
  const [importLoading, setImportLoading] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);
  const [updateLoading, setUpdateLoading] = useState(false);
  const [bulkDeleteLoading, setBulkDeleteLoading] = useState(false);
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [selectedRows, setSelectedRows] = useState([]);
  const [showBulkDeleteConfirm, setShowBulkDeleteConfirm] = useState(false);
  const [selectedRemark, setSelectedRemark] = useState("");
  const [editRemarkValue, setEditRemarkValue] = useState("");
  const [remarkUpdateLoading, setRemarkUpdateLoading] = useState(false);
  const [tatEditModalOpen, setTatEditModalOpen] = useState(false);
  const [tatEditValue, setTatEditValue] = useState("");
  const [tatUpdateLoading, setTatUpdateLoading] = useState(false);

  const [addressModalOpen, setAddressModalOpen] = useState(false);
  const [selectedAddress, setSelectedAddress] = useState("");
  const [taskIdEditModalOpen, setTaskIdEditModalOpen] = useState(false);
  const [taskIdEditValue, setTaskIdEditValue] = useState("");
  const [taskIdUpdateLoading, setTaskIdUpdateLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState("");

  const [fosEditModalOpen, setFosEditModalOpen] = useState(false);
  const [fosSearchValue, setFosSearchValue] = useState("");
  const [fosUpdateLoading, setFosUpdateLoading] = useState(false);
  const [fieldExecutives, setFieldExecutives] = useState([]);
  const [fosSearchLoading, setFosSearchLoading] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const { getFieldExecutivesData, loadingGetFieldExecutives } = useSelector(
    (state) => state.getFieldExecutives || {},
  );

  const [taskStatusEditModalOpen, setTaskStatusEditModalOpen] = useState(false);
  const [taskStatusEditValue, setTaskStatusEditValue] = useState("");
  const [taskStatusUpdateLoading, setTaskStatusUpdateLoading] = useState(false);

  const [importDate, setImportDate] = useState("");
  const [dates, setDates] = useState([]);
  const [filterDate, setFilterDate] = useState("");

  const [typeFilter, setTypeFilter] = useState("");
  const [currentTime, setCurrentTime] = useState("");

  const [tataViewOption, setTataViewOption] = useState(true);
  const [tataAddOption, setTataAddOption] = useState(true);
  const [tataUpdateOption, setTataUpdateOption] = useState(true);
  const [tataDeleteOption, setTataDeleteOption] = useState(true);

  // Export permission logic (like DelhiPage.js)
  let canExport = false;
  try {
    const userInfo = JSON.parse(localStorage.getItem("userInfo"));
    canExport = userInfo && userInfo.role && userInfo.role.usersAdd;
  } catch (e) {
    canExport = false;
  }

  const navigate = useNavigate();

  // Add these useEffect hooks after your existing useEffect declarations

  // Check for auth
  const userLogin = useSelector((state) => state.userLogin);
  const { loadingUserInfo, userInfo } = userLogin;

  useEffect(() => {
    // Check if user info exists
    if (!userInfo) {
      navigate("/");
    }
  }, [userInfo, navigate]);

  // Check for role permissions
  const getRoleInfo = useSelector((state) => state.getRoleInfo);
  const { loadingGetRole, getRoleData } = getRoleInfo;

  useEffect(() => {
    if (getRoleData) {
      // Use driveNbfcTata for TATA page access
      setTataViewOption(getRoleData.driveNbfcTata);
      setTataAddOption(getRoleData.driveNbfcTata);
      setTataUpdateOption(getRoleData.driveNbfcTata);
      setTataDeleteOption(getRoleData.driveNbfcTata);

      if (!getRoleData.driveNbfcTata) {
        toast("Not Authorized", {
          type: "error",
          hideProgressBar: true,
          autoClose: 2000,
        });
        navigate("/");
      }
    }
  }, [userInfo, getRoleData, navigate]);

  const PrimaryButton = ({
    children,
    onClick,
    disabled,
    className,
    loading,
  }) => (
    <Button
      onClick={onClick}
      disabled={disabled || loading}
      text={children}
      custom={`bg-blue-500 hover:bg-blue-700 ${className}`}
    />
  );

  const SecondaryButton = ({ children, onClick, className, disabled }) => (
    <Button
      onClick={onClick}
      disabled={disabled}
      text={children}
      custom={`bg-gray-500 hover:bg-gray-700 ${className}`}
    />
  );

  const DangerButton = ({ children, onClick, className, loading }) => (
    <Button
      onClick={onClick}
      disabled={loading}
      text={children}
      custom={`bg-red-500 hover:bg-red-700 ${className}`}
    />
  );

  const ModalActionButtons = ({ children, className }) => (
    <div className={`flex gap-2 justify-end ${className}`}>{children}</div>
  );

  const getCurrentISTDisplay = () => {
    const now = new Date();
    const utc = now.getTime() + now.getTimezoneOffset() * 60000;
    const istOffset = 5.5 * 60 * 60 * 1000;
    const currentISTTime = new Date(utc + istOffset);

    const options = {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hour12: true,
      timeZone: "Asia/Kolkata",
    };

    return currentISTTime.toLocaleDateString("en-IN", options);
  };

  const statusOptions = [
    { value: "", label: "All Status" },
    { value: "unassigned", label: "Unassigned" },
    { value: "visit pending", label: "Visit Pending" },
    { value: "finalisation pending", label: "Finalisation Pending" },
    { value: "in progress", label: "In Progress" },
    { value: "mapping", label: "Mapping" },
    { value: "not fired", label: "Not Fired" },
    { value: "waived", label: "Waived" },
    { value: "completed", label: "Completed" },
  ];

  const typeOptions = [
    { value: "", label: "All Types" },
    { value: "LOS", label: "LOS" },
    { value: "LS", label: "LS" },
    { value: "MAIL", label: "MAIL" },
  ];

  const filterControls = [
    {
      key: "date",
      label: "Date",
      control: (
        <select
          value={filterDate}
          onChange={(e) => setFilterDate(e.target.value)}
          className={driveFilterSelectClass}
        >
          <option value="">Select Date</option>
          {dates.map((date) => (
            <option key={date} value={date}>
              {date}
            </option>
          ))}
        </select>
      ),
    },
    {
      key: "status",
      label: "Status",
      control: (
        <select
          value={statusFilter}
          onChange={(e) => handleStatusFilter(e.target.value)}
          className={driveFilterSelectClass}
        >
          {statusOptions.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      ),
    },
    {
      key: "type",
      label: "Type",
      control: (
        <select
          value={typeFilter}
          onChange={(e) => handleTypeFilter(e.target.value)}
          className={driveFilterSelectClass}
        >
          {typeOptions.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      ),
    },
  ];

  const columns = [
    { field: "sno", header: "S No." },
    {
      field: "task_id",
      header: "Task ID",
      body: (rowData) => (
        <div className="flex items-center gap-2">
          <span className="truncate max-w-[60px]">
            {rowData.task_id || "-"}
          </span>
          <button
            onClick={() => {
              setTaskIdEditValue(rowData.task_id || "");
              setFormData(rowData);
              setTaskIdEditModalOpen(true);
            }}
            className="p-1 rounded text-blue-600 hover:text-blue-900 hover:bg-blue-50"
            title="Edit Task ID"
          >
            <Edit className="h-4 w-4" />
          </button>
        </div>
      ),
    },
    {
      field: "task_status",
      header: "Task Status",
      body: (rowData) => {
        const status = rowData.task_status;

        // Define color mapping (same as other pages)
        const getStatusColor = (status) => {
          if (!status) return "bg-gray-100 text-gray-600"; // Default for empty

          const statusLower = status.toLowerCase().trim();

          switch (statusLower) {
            case "unassigned":
              return "bg-red-100 text-red-800 border-red-200";
            case "visit pending":
              return "bg-orange-100 text-orange-800 border-orange-200";
            case "finalisation pending":
              return "bg-yellow-100 text-yellow-800 border-yellow-200";
            case "completed":
              return "bg-green-100 text-green-800 border-green-200";
            default:
              return "bg-gray-100 text-gray-600 border-gray-200";
          }
        };

        return (
          <div className="flex items-center gap-2 min-w-[160px]">
            <span
              className={`px-3 py-2 rounded-full text-sm font-medium border min-w-[120px] text-center ${getStatusColor(status)}`}
              title={status || "No Status"}
            >
              {status || "-"}
            </span>
            {/* <button
                            onClick={() => {
                                setTaskStatusEditValue(rowData.task_status || '');
                                setFormData(rowData);
                                setTaskStatusEditModalOpen(true);
                            }}
                            className="p-1 rounded text-blue-600 hover:text-blue-900 hover:bg-blue-50"
                            title="Edit Task Status"
                        >
                            <Edit className="h-4 w-4" />
                        </button> */}
          </div>
        );
      },
    },
    { field: "initiation_date", header: "Initiation Date" },
    { field: "code", header: "CODE" },
    { field: "los", header: "LOS" },
    { field: "name", header: "name" },
    { field: "fi_type", header: "FI TYPE" },
    { field: "product", header: "PRODUCT" },
    {
      field: "address",
      header: "ADDRESS",
      body: (rowData) => (
        <div className="flex items-center gap-2">
          <span className="truncate max-w-[100px]">
            {rowData.address?.length > 20
              ? rowData.address.slice(0, 20) + "..."
              : rowData.address || "-"}
          </span>
          <button
            onClick={() => {
              setSelectedAddress(rowData.address || "No address available");
              setAddressModalOpen(true);
            }}
            className="p-1 rounded text-blue-600 hover:text-blue-900 hover:bg-blue-50"
            title="View Address"
          >
            <Eye className="h-4 w-4" />
          </button>
        </div>
      ),
    },
    { field: "pincode", header: "pincode" },
    { field: "date", header: "Date" },
    { field: "time", header: "Time" },
    {
      field: "fos",
      header: "FOS",
      body: (rowData) => {
        // Hide FOS if status is "Sent To Bank"
        if (rowData.task_status === "Sent To Bank") {
          return <div className="text-gray-400">-</div>;
        }
        return (
          <div className="flex items-center gap-2">
            <span className="truncate max-w-[100px]">{rowData.fos || "-"}</span>
            <button
              onClick={() => {
                setFosSearchValue(rowData.fos || "");
                setFormData(rowData);
                setFosEditModalOpen(true);
                // Fetch initial field executives
                dispatch(getFieldExecutives(""));
              }}
              className="p-1 rounded text-blue-600 hover:text-blue-900 hover:bg-blue-50"
              title="Edit FOS"
            >
              <Edit className="h-4 w-4" />
            </button>
          </div>
        );
      },
    },

    { field: "zone", header: "zone" },
    { field: "type", header: "TYPE" },
    { field: "done", header: "done" },
    { field: "type_2", header: "TYPE 2" },
    { field: "d", header: "d" },
    { field: "branch", header: "BRANCH" },
    { field: "department", header: "DEPARTMENT" },
    {
      field: "tat",
      header: "TAT",
      body: (rowData) => (
        <div className="flex items-center gap-2">
          <span className="truncate max-w-[60px]">{rowData.tat || "-"}</span>
          <button
            onClick={() => {
              setTatEditValue(rowData.tat || "");
              setFormData(rowData);
              setTatEditModalOpen(true);
            }}
            className="p-1 rounded text-blue-600 hover:text-blue-900 hover:bg-blue-50"
            title="Edit TAT"
          >
            <Edit className="h-4 w-4" />
          </button>
        </div>
      ),
    },
    { field: "punch_time", header: "punch time" },
    { field: "punch_date", header: "PUNCH DATE" },
    { field: "revised_tat", header: "Revised TAT" },
    {
      field: "remark",
      header: "REMARK",
      body: (rowData) => {
        const remark = rowData.remark;
        return (
          <div className="flex items-center gap-2">
            <span className="truncate max-w-[100px]">
              {remark
                ? remark.length > 20
                  ? remark.slice(0, 20) + "..."
                  : remark
                : "-"}
            </span>
            <button
              onClick={() => {
                setEditRemarkValue(rowData.remark || "");
                setFormData(rowData);
                setViewModalOpen(true);
              }}
              className="p-1 rounded text-blue-600 hover:text-blue-900 hover:bg-blue-50"
              title="View remark"
            >
              <Eye className="h-4 w-4" />
            </button>
          </div>
        );
      },
    },
    {
      field: "status",
      header: "STATUS",
      body: (rowData) => (
        <span className="text-sm text-gray-700">{rowData.status || "-"}</span>
      ),
    },
    {
      field: "reason",
      header: "RESON",
      body: (rowData) => (
        <span
          className="text-sm text-gray-700 max-w-[150px] truncate block"
          title={rowData.reason || ""}
        >
          {rowData.reason
            ? rowData.reason.length > 30
              ? rowData.reason.slice(0, 30) + "..."
              : rowData.reason
            : "-"}
        </span>
      ),
    },
    { field: "reviewer_name", header: "Reviewer Name2" },
    { field: "area_zone_1", header: "AREA - ZONE1" },
    { field: "notes1", header: "NOTES1" },
    { field: "time_in", header: "TIME IN" },
    { field: "reviewer_name_2", header: "Reviewr Name " },
    { field: "bank_name", header: "Bank Name" },
    { field: "pending_in_golog", header: "Pending in Golog" },
    { field: "tat_category", header: "TAT Category" },
    { field: "extra_name", header: "Name " },
  ];

  const copyToClipboard = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success("Remark copied to clipboard!");
    } catch (err) {
      console.error("Failed to copy: ", err);
      toast.error("Failed to copy remark");
    }
  };

  const handleTypeFilter = (selectedType) => {
    console.log("TATA Type filter changed to:", selectedType);
    setTypeFilter(selectedType);
    setPage(1);
  };

  const refreshData = async () => {
    try {
      // Sync task statuses, FOS, and remarks first
      console.log("Syncing TATA task data before refresh...");
      const syncResult = await dispatch(syncTataTaskStatus());

      // If sync returned updated records, update local state first
      if (
        syncResult &&
        syncResult.updatedRecords &&
        syncResult.updatedRecords.length > 0
      ) {
        setData((prevData) => {
          const updatedData = [...prevData];
          syncResult.updatedRecords.forEach((update) => {
            const recordIndex = updatedData.findIndex(
              (record) => record._id === update._id,
            );
            if (recordIndex !== -1) {
              // Update status if it changed
              if (update.new_status) {
                updatedData[recordIndex] = {
                  ...updatedData[recordIndex],
                  task_status: update.new_status,
                };
              }

              // Update FOS if it changed
              if (update.new_fos) {
                updatedData[recordIndex] = {
                  ...updatedData[recordIndex],
                  fos: update.new_fos,
                };
              }

              // Update remark if it changed
              if (update.new_remark) {
                updatedData[recordIndex] = {
                  ...updatedData[recordIndex],
                  remark: update.new_remark,
                };
              }
              if (update.new_status_field) {
                updatedData[recordIndex] = {
                  ...updatedData[recordIndex],
                  status: update.new_status_field,
                };
              }

              if (update.new_reason) {
                updatedData[recordIndex] = {
                  ...updatedData[recordIndex],
                  reason: update.new_reason,
                };
              }
            }
          });
          return updatedData;
        });
      }

      // Then fetch updated data
      const queryParams = new URLSearchParams();
      if (search) queryParams.append("search", search);
      queryParams.append("page", page);
      queryParams.append("limit", 10);
      if (filterDate) queryParams.append("importDate", filterDate);
      else queryParams.append("importDate", importDate);
      if (statusFilter) queryParams.append("status", statusFilter);
      if (typeFilter) queryParams.append("typeFilter", typeFilter);
      dispatch(fetchAllTataRecords(queryParams.toString()));
    } catch (error) {
      console.error("Error during TATA sync in refreshData:", error);
      // Still fetch data even if sync fails
      const queryParams = new URLSearchParams();
      if (search) queryParams.append("search", search);
      queryParams.append("page", page);
      queryParams.append("limit", 10);
      if (filterDate) queryParams.append("importDate", filterDate);
      else queryParams.append("importDate", importDate);
      if (statusFilter) queryParams.append("status", statusFilter);
      if (typeFilter) queryParams.append("typeFilter", typeFilter);
      dispatch(fetchAllTataRecords(queryParams.toString()));
    }
  };

  // Sync on page load/refresh only (automatic interval sync disabled)
  useEffect(() => {
    const performRealtimeSync = async () => {
      try {
        console.log("Starting TATA realtime sync...");

        // Call the sync endpoint
        const syncResult = await dispatch(syncTataTaskStatus());

        // If there are updated records, update the local state
        if (
          syncResult &&
          syncResult.updatedRecords &&
          syncResult.updatedRecords.length > 0
        ) {
          console.log(
            `Found ${syncResult.updatedRecords.length} updated TATA records`,
          );

          // Update local data state with new statuses, FOS, and remarks
          setData((prevData) => {
            const updatedData = [...prevData];
            syncResult.updatedRecords.forEach((update) => {
              const recordIndex = updatedData.findIndex(
                (record) => record._id === update._id,
              );
              if (recordIndex !== -1) {
                // Update status if it changed
                if (update.new_status) {
                  updatedData[recordIndex] = {
                    ...updatedData[recordIndex],
                    task_status: update.new_status,
                  };
                }

                // Update FOS if it changed
                if (update.new_fos) {
                  updatedData[recordIndex] = {
                    ...updatedData[recordIndex],
                    fos: update.new_fos,
                  };
                }

                // Update remark if it changed
                if (update.new_remark) {
                  updatedData[recordIndex] = {
                    ...updatedData[recordIndex],
                    remark: update.new_remark,
                  };
                }
              }
            });
            return updatedData;
          });

          // Enhanced toast notification
          const statusUpdates = syncResult.updatedRecords.filter(
            (r) => r.new_status,
          ).length;
          const fosUpdates = syncResult.updatedRecords.filter(
            (r) => r.new_fos,
          ).length;
          const remarkUpdates = syncResult.updatedRecords.filter(
            (r) => r.new_remark,
          ).length;

          let message = "";
          if (statusUpdates > 0) message += `${statusUpdates} status`;
          if (fosUpdates > 0)
            message += `${message ? ", " : ""}${fosUpdates} FOS`;
          if (remarkUpdates > 0)
            message += `${message ? ", " : ""}${remarkUpdates} remark`;

          // if (message) {
          //     toast.success(`TATA: ${message} updated automatically`, {
          //         position: "bottom-right",
          //         autoClose: 3000,
          //     });
          // }
        }

        setLastRefresh(new Date());
      } catch (error) {
        console.error("TATA realtime sync failed:", error);
        // Fallback to full refresh if realtime sync fails
        refreshData();
      }
    };

    // Sync ONLY on initial page load (not on filter/page changes)
    performRealtimeSync();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    let interval;
    if (tatEditModalOpen) {
      interval = setInterval(() => {
        setCurrentTime(getCurrentISTDisplay());
      }, 1000);
    }
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [tatEditModalOpen]);

  // Helper to get last 3 IST dates in YYYY-MM-DD, excluding today
  function getLast5IstdatesExcludeToday() {
    const dates = [];
    const now = new Date();
    const utc = now.getTime() + now.getTimezoneOffset() * 60000;
    const istOffset = 5.5 * 60 * 60 * 1000;
    // Include today (i = 0) and go back 6 more days for total of 7 days
    for (let i = 0; i <= 6; i++) {
      const istTime = new Date(utc + istOffset - i * 24 * 60 * 60 * 1000);
      const yyyy = istTime.getFullYear();
      const mm = String(istTime.getMonth() + 1).padStart(2, "0");
      const dd = String(istTime.getDate()).padStart(2, "0");
      dates.push(`${yyyy}-${mm}-${dd}`);
    }
    return dates;
  }

  useEffect(() => {
    console.log("getFieldExecutivesData changed:", getFieldExecutivesData);
    if (getFieldExecutivesData?.data) {
      console.log("Setting fieldExecutives:", getFieldExecutivesData.data);
      setFieldExecutives(getFieldExecutivesData.data);
    }
  }, [getFieldExecutivesData]);

  // Add this callback function
  const handleFosSearch = useCallback(
    async (searchTerm) => {
      console.log("handleFosSearch called with:", searchTerm);
      setFosSearchValue(searchTerm);

      if (searchTerm.length > 0) {
        try {
          setFosSearchLoading(true);
          console.log("Dispatching getFieldExecutives...");

          const result = await dispatch(getFieldExecutives(searchTerm));
          console.log("getFieldExecutives result:", result);

          if (result && result.data) {
            setFieldExecutives(result.data);
            setShowSuggestions(true);
          } else {
            setFieldExecutives([]);
            setShowSuggestions(false);
          }
        } catch (error) {
          console.error("Error searching field executives:", error);
          setFieldExecutives([]);
          setShowSuggestions(false);
        } finally {
          setFosSearchLoading(false);
        }
      } else {
        setFieldExecutives([]);
        setShowSuggestions(false);
      }
    },
    [dispatch],
  );

  useEffect(() => {
    setDates(getLast5IstdatesExcludeToday());
    const now = new Date();
    const utc = now.getTime() + now.getTimezoneOffset() * 60000;
    const istOffset = 5.5 * 60 * 60 * 1000;
    const istTime = new Date(utc + istOffset);
    const yyyy = istTime.getFullYear();
    const mm = String(istTime.getMonth() + 1).padStart(2, "0");
    const dd = String(istTime.getDate()).padStart(2, "0");
    setImportDate(`${yyyy}-${mm}-${dd}`);
    setFilterDate("");
    setStatusFilter(""); // Clear status filter
    setTypeFilter(""); // ADD THIS LINE
  }, []);

  useEffect(() => {
    const validPage = Math.max(1, parseInt(page) || 1);
    if (validPage !== page) {
      setPage(validPage);
      return;
    }
    const queryParams = new URLSearchParams();
    if (search) queryParams.append("search", search);
    queryParams.append("page", validPage);
    queryParams.append("limit", 10);
    if (filterDate) queryParams.append("importDate", filterDate);
    else queryParams.append("importDate", importDate);
    if (statusFilter) queryParams.append("status", statusFilter);
    if (typeFilter) queryParams.append("typeFilter", typeFilter);
    dispatch(fetchAllTataRecords(queryParams.toString()));
  }, [
    dispatch,
    search,
    page,
    importDate,
    filterDate,
    statusFilter,
    typeFilter,
  ]);

  useEffect(() => {
    setData(getTataRecordsData?.data || []);
  }, [getTataRecordsData]);

  const handleSearch = (searchTerm) => {
    setSearch(searchTerm);
    setPage(1);
  };

  const handleStatusFilter = (selectedStatus) => {
    setStatusFilter(selectedStatus);
    setPage(1);
  };

  const handleBulkDelete = async () => {
    if (selectedRows.length === 0) {
      toast.error("Please select rows to delete");
      return;
    }
    try {
      setBulkDeleteLoading(true);
      const idsArray = selectedRows.map((row) => row._id);
      await dispatch(bulkDeleteTataRecords(idsArray));
      setSelectedRows([]);
      setShowBulkDeleteConfirm(false);
      toast.success("Bulk delete successful!");
      refreshData();
    } catch (err) {
      console.error("Bulk Delete Error:", err);
      toast.error(
        "Delete failed: " + (err.response?.data?.message || err.message),
      );
    } finally {
      setBulkDeleteLoading(false);
    }
  };

  const handleImport = async () => {
    if (!file) {
      toast.error("Please select a file to import");
      return;
    }
    try {
      setImportLoading(true);
      await dispatch(importTataRecords(file));
      setModalOpen(false);
      setFile(null);
      toast.success("Import Success!");
      refreshData();
    } catch (err) {
      console.error("Import Error:", err.message);
      toast.error(
        "Import Failed: " + (err.response?.data?.message || err.message),
      );
    } finally {
      setImportLoading(false);
    }
  };

  const handleExport = async () => {
    try {
      setExportLoading(true);
      await dispatch(exportTataRecords());
    } catch (err) {
      console.error("Export Error:", err.message);
      toast.error(
        "Export Failed: " + (err.response?.data?.message || err.message),
      );
    } finally {
      setExportLoading(false);
    }
  };

  const handleEdit = (row) => {
    setFormData(row);
    setEditModalOpen(true);
  };

  const handleUpdate = async () => {
    try {
      setUpdateLoading(true);
      await dispatch(updateTataRecord(`${formData._id}`, formData));
      setEditModalOpen(false);
      toast.success("Update successful!");
      refreshData();
    } catch (err) {
      console.error("Update Error:", err.message);
      toast.error(
        "Update Failed: " + (err.response?.data?.message || err.message),
      );
    } finally {
      setUpdateLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm("Are you sure you want to delete this entry?")) {
      try {
        await dispatch(deleteTataRecord(id));
        toast.success("Delete successful!");
        refreshData();
      } catch (err) {
        toast.error(
          "Delete Failed: " + (err.response?.data?.message || err.message),
        );
      }
    }
  };

  const actions = [
    {
      icon: <Edit className="h-4 w-4" />,
      onClick: (row) => handleEdit(row),
    },
    {
      icon: <Trash2 className="h-4 w-4" />,
      onClick: (row) => handleDelete(row._id),
    },
  ];

  // Add formatted initiation_date to each row (using date field - initiation date from Excel)
  const formattedData = (data || []).map((row) => ({
    ...row,
    initiation_date: row.date
      ? (() => {
          const dateObj = new Date(row.date);
          if (!isNaN(dateObj.getTime())) {
            const day = String(dateObj.getDate()).padStart(2, "0");
            const month = String(dateObj.getMonth() + 1).padStart(2, "0");
            const year = String(dateObj.getFullYear()).slice(-2);
            return `${day}-${month}-${year}`;
          }
          return "-";
        })()
      : "-",
  }));

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 mb-6">
          <div className="px-6 py-4 border-b border-gray-200">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <div>
                <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
                  TATA Records
                </h1>
                <p className="text-sm text-gray-600 mt-1">
                  Manage and view TATA corporate records data • Click any cell
                  to edit inline
                </p>
              </div>
              <div className="flex items-center gap-2">
                {selectedRows.length > 0 && (
                  <>
                    <span className="text-sm text-indigo-600">
                      {selectedRows.length} selected
                    </span>
                    <DangerButton
                      onClick={() => setShowBulkDeleteConfirm(true)}
                      className="flex items-center gap-2"
                      disabled={bulkDeleteLoading}
                    >
                      <Trash className="h-4 w-4" />
                      Delete Selected
                    </DangerButton>
                  </>
                )}
                <PrimaryButton
                  onClick={() => setModalOpen(true)}
                  className="flex items-center gap-2"
                >
                  <Upload className="h-4 w-4" />
                  Import
                </PrimaryButton>
                {canExport && (
                  <PrimaryButton
                    onClick={handleExport}
                    className="flex items-center gap-2"
                  >
                    <Download className="h-4 w-4" />
                    Export
                  </PrimaryButton>
                )}
              </div>
            </div>
          </div>

          <DriveFilters
            searchValue={search}
            onSearchChange={handleSearch}
            searchPlaceholder="Search by name,zone,los,fos,code,bank name,reviewer..."
            filters={filterControls}
            total={total}
            columnsClass="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3"
          />
          {selectedRows.length > 0 && (
            <div className="px-6 pb-4">
              <div className="inline-flex items-center gap-2 p-2 bg-indigo-50 border border-indigo-200 rounded text-sm text-indigo-700">
                ✓ {selectedRows.length} record
                {selectedRows.length !== 1 ? "s" : ""} selected
              </div>
            </div>
          )}
        </div>

        {/* Table Container */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200">
          <div className="overflow-hidden">
            {loading ? (
              <div className="py-12">
                <Loading />
              </div>
            ) : (
              <div className="overflow-x-auto">
                <Table
                  data={formattedData}
                  columns={columns}
                  loading={loading}
                  pagination={{ totalRecords: total }}
                  currentPage={page}
                  onPageChange={setPage}
                  actions={actions} // From your existing actions array
                  selectable={true}
                  selectedRows={selectedRows}
                  onSelectionChange={setSelectedRows} // Debug log
                />
              </div>
            )}
          </div>

          {/* Pagination */}
        </div>

        {/* Bulk Delete Confirmation Modal */}
        {showBulkDeleteConfirm && (
          <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
            <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
              <div className="mt-3">
                <h3 className="text-lg font-medium text-gray-900 text-center mb-4">
                  Delete Selected Records
                </h3>
                <p className="text-sm text-gray-500 text-center mb-6">
                  Are you sure you want to delete {selectedRows.length} selected
                  records? This action cannot be undone.
                </p>
                <ModalActionButtons>
                  <DangerButton
                    onClick={handleBulkDelete}
                    className="flex items-center gap-2"
                    loading={bulkDeleteLoading}
                  >
                    <Trash className="h-4 w-4" />
                    Delete {selectedRows.length} Records
                  </DangerButton>
                  <SecondaryButton
                    onClick={() => setShowBulkDeleteConfirm(false)}
                  >
                    Cancel
                  </SecondaryButton>
                </ModalActionButtons>
              </div>
            </div>
          </div>
        )}

        {/* Import/Export Modal */}
        {modalOpen && (
          <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
            <div className="relative top-10 mx-auto p-5 border w-full max-w-4xl shadow-lg rounded-md bg-white m-4">
              <div className="mt-3">
                <h3 className="text-lg font-medium text-gray-900 mb-4">
                  Import / Export Data
                </h3>
                {/* Template Download Section */}
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
                  <h4 className="text-md font-semibold text-blue-900 mb-3 flex items-center gap-2">
                    📋 Excel Template Format
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                    <div>
                      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 mb-3">
                        <h5 className="text-sm font-semibold text-yellow-800 mb-2">
                          ⚠️ Important Notes:
                        </h5>
                        <ul className="text-xs text-yellow-700 space-y-1">
                          <li>• Column names are case-sensitive</li>
                          <li>• Date format: DD/MM/YYYY</li>
                          <li>• Time format: HH:MM AM/PM</li>
                          <li>• TAT should be numeric values</li>
                          <li>
                            • Empty cells will be treated as empty strings
                          </li>
                          <li>• '#N/A' values will be preserved as-is</li>
                          <li>• Zone field defaults to '#N/A' if empty</li>
                        </ul>
                      </div>
                      <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                        <h5 className="text-sm font-semibold text-green-800 mb-2">
                          ✅ Download Template:
                        </h5>
                        <p className="text-xs text-green-700 mb-3">
                          Click below to download a sample Excel template with
                          the correct column structure.
                        </p>
                        <button
                          onClick={() => {
                            const link = document.createElement("a");
                            link.href = "/tata_template.xlsx";
                            link.download = "TATA_Import_Template.xlsx";
                            link.click();
                          }}
                          className="w-full bg-green-600 hover:bg-green-700 text-white text-sm font-medium py-2 px-4 rounded-md transition-colors duration-200 flex items-center justify-center gap-2"
                        >
                          📥 Download Template
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
                {/* File Upload Section */}
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Select file to import
                    </label>
                    <input
                      type="file"
                      accept=".xlsx,.xls,.csv"
                      onChange={(e) => setFile(e.target.files[0])}
                      className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-indigo-50 file:text-indigo-700 hover:file:bg-indigo-100"
                    />
                  </div>
                </div>
                <ModalActionButtons className="mt-6">
                  <PrimaryButton
                    onClick={handleImport}
                    disabled={!file}
                    loading={importLoading}
                    className="flex items-center gap-2"
                  >
                    <Upload className="h-4 w-4" />
                    Import
                  </PrimaryButton>
                  {canExport && (
                    <PrimaryButton
                      onClick={handleExport}
                      loading={exportLoading}
                      className="flex items-center gap-2"
                    >
                      <Download className="h-4 w-4" />
                      Export
                    </PrimaryButton>
                  )}
                  <SecondaryButton onClick={() => setModalOpen(false)}>
                    Cancel
                  </SecondaryButton>
                </ModalActionButtons>
              </div>
            </div>
          </div>
        )}

        {/* Edit Modal */}
        {editModalOpen && (
          <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
            <div className="relative top-10 mx-auto p-5 border w-full max-w-2xl shadow-lg rounded-md bg-white m-4">
              <div className="mt-3">
                <h3 className="text-lg font-medium text-gray-900 mb-4">
                  Edit Record
                </h3>

                <div className="max-h-96 overflow-y-auto">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {Object.keys(formData)
                      .filter(
                        (field) =>
                          !["_id", "__v", "createdAt", "updatedAt"].includes(
                            field,
                          ),
                      )
                      .map((field) => (
                        <div key={field}>
                          <label className="block text-sm font-medium text-gray-700 mb-1 capitalize">
                            {field.replace(/_/g, " ")}
                          </label>
                          <input
                            type="text"
                            className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                            value={formData[field] || ""}
                            onChange={(e) =>
                              setFormData({
                                ...formData,
                                [field]: e.target.value,
                              })
                            }
                          />
                        </div>
                      ))}
                  </div>
                </div>

                <ModalActionButtons className="mt-6">
                  <PrimaryButton
                    onClick={handleUpdate}
                    className="flex items-center gap-2"
                    loading={updateLoading}
                  >
                    <Edit className="h-4 w-4" />
                    Update
                  </PrimaryButton>
                  <SecondaryButton onClick={() => setEditModalOpen(false)}>
                    Cancel
                  </SecondaryButton>
                </ModalActionButtons>
              </div>
            </div>
          </div>
        )}
        {viewModalOpen && (
          <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
            <div className="relative top-20 mx-auto p-5 border w-full max-w-4xl shadow-lg rounded-md bg-white">
              <div className="mt-3">
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-lg font-medium text-gray-900 flex items-center gap-2">
                    <Eye className="h-5 w-5" />
                    Edit Remark
                  </h3>

                  <div className="flex items-center gap-2">
                    <PrimaryButton
                      onClick={() => copyToClipboard(editRemarkValue)}
                      className="flex items-center gap-2"
                    >
                      <Copy className="h-4 w-4" />
                      Copy
                    </PrimaryButton>

                    <PrimaryButton
                      onClick={async () => {
                        try {
                          setRemarkUpdateLoading(true);
                          const updated = {
                            ...formData,
                            remark: editRemarkValue,
                          };
                          await dispatch(
                            updateTataRecord(`${formData._id}`, updated),
                          );
                          setViewModalOpen(false);
                          refreshData();
                          toast.success("Remark updated successfully!");
                        } catch (err) {
                          console.error(err);
                          toast.error("Failed to update remark");
                        } finally {
                          setRemarkUpdateLoading(false);
                        }
                      }}
                      loading={remarkUpdateLoading}
                      className="flex items-center gap-2"
                    >
                      Save
                    </PrimaryButton>

                    <button
                      onClick={() => setViewModalOpen(false)}
                      disabled={remarkUpdateLoading}
                      className="p-2 rounded text-gray-600 hover:text-gray-900 hover:bg-gray-100"
                      title="Close"
                    >
                      <X className="h-5 w-5" />
                    </button>
                  </div>
                </div>

                <div
                  className="bg-gray-50 p-4 rounded-md border"
                  style={{ height: "400px" }}
                >
                  <textarea
                    value={editRemarkValue}
                    onChange={(e) => setEditRemarkValue(e.target.value)}
                    className="w-full h-full p-0 border-none bg-transparent text-sm text-gray-700 whitespace-pre-wrap leading-relaxed resize-none focus:outline-none overflow-hidden"
                    style={{ fontFamily: "inherit" }}
                  />
                </div>
              </div>
            </div>
          </div>
        )}
        {tatEditModalOpen && (
          <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
            <div className="relative top-20 mx-auto p-5 border w-full max-w-md shadow-lg rounded-md bg-white">
              <div className="mt-3">
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-lg font-medium text-gray-900 flex items-center gap-2">
                    <Edit className="h-5 w-5" />
                    Edit TAT
                  </h3>
                  <div className="flex items-center gap-2">
                    <PrimaryButton
                      onClick={async () => {
                        try {
                          setTatUpdateLoading(true);

                          // Get current IST time when TAT is filled
                          const now = new Date();
                          const utc =
                            now.getTime() + now.getTimezoneOffset() * 60000;
                          const istOffset = 5.5 * 60 * 60 * 1000;
                          const currentISTTime = new Date(utc + istOffset);

                          // Format punch_date (YYYY-MM-DD for database)
                          const yyyy = currentISTTime.getFullYear();
                          const mm = String(
                            currentISTTime.getMonth() + 1,
                          ).padStart(2, "0");
                          const dd = String(currentISTTime.getDate()).padStart(
                            2,
                            "0",
                          );
                          const formattedPunchDate = `${yyyy}-${mm}-${dd}`;

                          // Format punch_time (HH:MM AM/PM)
                          let hours = currentISTTime.getHours();
                          const minutes = currentISTTime.getMinutes();
                          const ampm = hours >= 12 ? "PM" : "AM";
                          hours = hours % 12 || 12;
                          const formattedPunchTime = `${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")} ${ampm}`;

                          const updated = {
                            ...formData,
                            tat: tatEditValue,
                            punch_date: formattedPunchDate,
                            punch_time: formattedPunchTime,
                            // Automatically set task_status to "Sent To Bank" when TAT is filled
                            task_status:
                              tatEditValue && tatEditValue.trim() !== ""
                                ? "Sent To Bank"
                                : formData.task_status,
                            manualTaskStatus: true, // Mark as manually modified
                          };

                          await dispatch(
                            updateTataRecord(`${formData._id}`, updated),
                          );
                          setTatEditModalOpen(false);
                          refreshData();
                          toast.success(
                            'TAT updated with current punch time and status set to "Sent To Bank"!',
                          );
                        } catch (err) {
                          console.error(err);
                          toast.error("Failed to update TAT");
                        } finally {
                          setTatUpdateLoading(false);
                        }
                      }}
                      loading={tatUpdateLoading}
                      className="flex items-center gap-2"
                    >
                      Save
                    </PrimaryButton>
                    <SecondaryButton
                      onClick={() => setTatEditModalOpen(false)}
                      disabled={tatUpdateLoading}
                    >
                      Cancel
                    </SecondaryButton>
                  </div>
                </div>

                <div className="bg-gray-50 p-4 rounded-md border">
                  <input
                    type="number"
                    value={tatEditValue}
                    onChange={(e) => setTatEditValue(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                    placeholder="Enter TAT "
                  />
                  <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded">
                    <div className="text-sm">
                      <span className="font-medium text-blue-800">
                        Current IST Time:{" "}
                      </span>
                      <span className="text-blue-700">
                        {getCurrentISTDisplay()}
                      </span>
                    </div>
                    <div className="text-xs text-blue-600 mt-1">
                      This time will be captured as punch time when you save
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
        {addressModalOpen && (
          <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
            <div className="relative top-20 mx-auto p-5 border w-full max-w-3xl shadow-lg rounded-md bg-white">
              <div className="mt-3">
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-lg font-medium text-gray-900 flex items-center gap-2">
                    <Eye className="h-5 w-5" />
                    View Address
                  </h3>
                  <SecondaryButton onClick={() => setAddressModalOpen(false)}>
                    Close
                  </SecondaryButton>
                </div>

                <div
                  className="bg-gray-50 p-4 rounded-md border"
                  style={{ height: "300px" }}
                >
                  <p className="text-sm text-gray-700 whitespace-pre-wrap leading-relaxed">
                    {selectedAddress}
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}
        {/* Task ID Edit Modal */}
        {taskIdEditModalOpen && (
          <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
            <div className="relative top-20 mx-auto p-5 border w-full max-w-md shadow-lg rounded-md bg-white">
              <div className="mt-3">
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-lg font-medium text-gray-900 flex items-center gap-2">
                    <Edit className="h-5 w-5" />
                    Edit Task ID
                  </h3>
                  <div className="flex items-center gap-2">
                    <PrimaryButton
                      onClick={async () => {
                        try {
                          setTaskIdUpdateLoading(true);
                          const updated = {
                            ...formData,
                            task_id: taskIdEditValue,
                          };
                          await dispatch(
                            updateTataRecord(formData._id, updated),
                          );
                          setTaskIdEditModalOpen(false);
                          refreshData();
                          toast.success("Task ID updated successfully!");
                        } catch (err) {
                          console.error(err);
                          toast.error("Failed to update Task ID");
                        } finally {
                          setTaskIdUpdateLoading(false);
                        }
                      }}
                      loading={taskIdUpdateLoading}
                      className="flex items-center gap-2"
                    >
                      Save
                    </PrimaryButton>
                    <SecondaryButton
                      onClick={() => setTaskIdEditModalOpen(false)}
                      disabled={taskIdUpdateLoading}
                    >
                      Cancel
                    </SecondaryButton>
                  </div>
                </div>
                <div className="bg-gray-50 p-4 rounded-md border">
                  <input
                    type="number"
                    value={taskIdEditValue}
                    onChange={(e) => setTaskIdEditValue(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                    placeholder="Enter Task ID"
                  />
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Task Status Edit Modal */}
        {taskStatusEditModalOpen && (
          <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
            <div className="relative top-20 mx-auto p-5 border w-full max-w-md shadow-lg rounded-md bg-white">
              <div className="mt-3">
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-lg font-medium text-gray-900 flex items-center gap-2">
                    <Edit className="h-5 w-5" />
                    Edit Task Status
                  </h3>
                  <div className="flex items-center gap-2">
                    <PrimaryButton
                      onClick={async () => {
                        try {
                          setTaskStatusUpdateLoading(true);
                          const updated = {
                            ...formData,
                            task_status: taskStatusEditValue,
                          };
                          await dispatch(
                            updateTataRecord(formData._id, updated),
                          );
                          setTaskStatusEditModalOpen(false);
                          refreshData();
                          toast.success("Task Status updated successfully!");
                        } catch (err) {
                          console.error(err);
                          toast.error("Failed to update Task Status");
                        } finally {
                          setTaskStatusUpdateLoading(false);
                        }
                      }}
                      loading={taskStatusUpdateLoading}
                      className="flex items-center gap-2"
                    >
                      Save
                    </PrimaryButton>
                    <SecondaryButton
                      onClick={() => setTaskStatusEditModalOpen(false)}
                      disabled={taskStatusUpdateLoading}
                    >
                      Cancel
                    </SecondaryButton>
                  </div>
                </div>
                <div className="bg-gray-50 p-4 rounded-md border">
                  <input
                    type="text"
                    value={taskStatusEditValue}
                    onChange={(e) => setTaskStatusEditValue(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                    placeholder="Enter Task Status"
                  />
                </div>
              </div>
            </div>
          </div>
        )}
        {/* FOS Edit Modal */}
        {fosEditModalOpen && (
          <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
            <div className="relative top-20 mx-auto p-5 border w-full max-w-md shadow-lg rounded-md bg-white">
              <div className="mt-3">
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-lg font-medium text-gray-900 flex items-center gap-2">
                    <Edit className="h-5 w-5" />
                    Edit FOS
                  </h3>
                  <div className="flex items-center gap-2">
                    <PrimaryButton
                      onClick={async () => {
                        try {
                          setFosUpdateLoading(true);
                          const updated = { ...formData, fos: fosSearchValue };
                          await dispatch(
                            updateTataRecord(formData._id, updated),
                          );
                          setFosEditModalOpen(false);
                          setShowSuggestions(false);
                          refreshData();
                          toast.success("FOS updated successfully!");
                        } catch (err) {
                          console.error(err);
                          toast.error("Failed to update FOS");
                        } finally {
                          setFosUpdateLoading(false);
                        }
                      }}
                      loading={fosUpdateLoading}
                      className="flex items-center gap-2"
                    >
                      Save
                    </PrimaryButton>
                    <SecondaryButton
                      onClick={() => {
                        setFosEditModalOpen(false);
                        setShowSuggestions(false);
                        setFieldExecutives([]);
                      }}
                      disabled={fosUpdateLoading}
                    >
                      Cancel
                    </SecondaryButton>
                  </div>
                </div>

                <div className="bg-gray-50 p-4 rounded-md border relative">
                  <div className="relative">
                    <input
                      type="text"
                      value={fosSearchValue}
                      onChange={(e) => handleFosSearch(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                      placeholder="Search Field Executive..."
                      autoComplete="off"
                    />

                    {fosSearchLoading && (
                      <div className="absolute right-3 top-3">
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-indigo-600"></div>
                      </div>
                    )}

                    {/* Suggestions Dropdown */}
                    {showSuggestions && fieldExecutives.length > 0 && (
                      <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-48 overflow-y-auto">
                        {fieldExecutives.map((executive) => (
                          <div
                            key={executive._id}
                            className="px-3 py-2 hover:bg-gray-100 cursor-pointer border-b border-gray-100 last:border-b-0"
                            onClick={() => {
                              setFosSearchValue(executive.name);
                              setShowSuggestions(false);
                            }}
                          >
                            <div className="text-sm font-medium text-gray-900">
                              {executive.name}
                            </div>
                            <div className="text-xs text-gray-500">
                              {executive.email} • {executive.identifier}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}

                    {showSuggestions &&
                      fieldExecutives.length === 0 &&
                      fosSearchValue.length > 0 &&
                      !fosSearchLoading && (
                        <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg">
                          <div className="px-3 py-2 text-sm text-gray-500">
                            No Field Executives found
                          </div>
                        </div>
                      )}
                  </div>

                  <div className="mt-3 text-xs text-gray-500">
                    Type to search for Field Executive users
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default TataPage;
